import React from 'react';
import { Box, Typography } from '@material-ui/core';
import { ChevronRight, Email as EmailIcon, PhoneIphone as PhoneIcon } from '@material-ui/icons';
import { unpackToDateTime } from '@campfire/hot-date';
import { RecipientAvatars } from './RecipientAvatars';
import {
  CommunicationLogItem,
  CommunicationsPerson,
  humaniseTag,
  personName,
  rosterStatus,
  sentStatuses,
} from './communications-model';
import { CommunicationStatusChip } from '../../../common/communications/CommunicationStatusChip';

interface CommunicationLogRowProps {
  item: CommunicationLogItem;
  sender?: CommunicationsPerson;
  recipients?: CommunicationsPerson[];
  onOpen: (item: CommunicationLogItem) => void;
  selected?: boolean;
}

const sentLabel = (sentAt: string) => {
  const sent = unpackToDateTime(sentAt);
  const now = unpackToDateTime(new Date().toISOString());
  const { days } = now.diff(sent, 'days');
  if (days < 1) return sent.toFormat('h:mm a');
  if (days < 2) return 'Yesterday';
  if (days < 7) return sent.toFormat('ccc h:mm a');
  return sent.toFormat('d LLL');
};

const Tag = ({ label, outlined }: { label: string; outlined?: boolean }) => (
  <Box
    style={{
      backgroundColor: outlined ? 'transparent' : '#EFEFEF',
      border: outlined ? '1px solid #d9d9d9' : '1px solid transparent',
      borderRadius: 3,
      padding: '2px 8px',
    }}
  >
    <Typography style={{ fontSize: 10, fontWeight: 600, color: '#545454' }}>{label}</Typography>
  </Box>
);

const ChannelChip = ({ channel }: { channel: 'sms' | 'email' }) => (
  <Box
    display='flex'
    alignItems='center'
    style={{ backgroundColor: '#d9d9d9', borderRadius: 3, padding: '0 8px', height: 22, boxSizing: 'border-box' }}
  >
    {channel === 'sms' ? (
      <PhoneIcon style={{ width: 10, height: 10, color: '#545454', marginRight: 4 }} />
    ) : (
      <EmailIcon style={{ width: 10, height: 10, color: '#545454', marginRight: 4 }} />
    )}
    <Typography style={{ fontSize: 10, fontWeight: 600, color: '#545454' }}>
      {channel === 'sms' ? 'SMS' : 'Email'}
    </Typography>
  </Box>
);

export const CommunicationLogRow = ({ item, sender, recipients = [], onOpen, selected }: CommunicationLogRowProps) => {
  const isRoster = item.kind === 'roster';
  const channel: 'sms' | 'email' = isRoster ? 'sms' : item.sent.channel;

  const recipientCount = isRoster ? 1 : item.sent.recipientCount;
  const single = recipients.length === 1 && recipientCount === 1 ? recipients[0] : undefined;
  const title = isRoster
    ? item.roster.volunteerName
    : (single && personName(single)) || (recipientCount === 1 && item.sent.recipient) || `${recipientCount} recipients`;

  const preview = isRoster ? item.roster.preview : item.sent.preview;
  const tag = isRoster ? item.roster.activityName : humaniseTag(item.sent.tag);
  const sentByLabel = sender ? personName(sender) : 'System';
  const statuses = isRoster ? [{ status: rosterStatus(item.roster), count: undefined }] : sentStatuses(item.sent);

  return (
    <Box
      display='flex'
      alignItems='center'
      borderBottom='1px solid #EFEFEF'
      padding='12px 16px'
      style={{ cursor: 'pointer', backgroundColor: selected ? '#F5F5F5' : undefined }}
      onClick={() => onOpen(item)}
    >
      <RecipientAvatars people={recipients} totalCount={recipientCount} />

      <Box flexGrow={1} minWidth={0} paddingLeft='12px' paddingRight='12px'>
        <Box display='flex' alignItems='baseline' style={{ gap: 8 }}>
          <Typography noWrap style={{ fontSize: 14, fontWeight: 600, color: '#444444' }}>
            {title}
          </Typography>
          <Typography noWrap style={{ fontSize: 12, color: '#646464' }}>
            {sentByLabel}
          </Typography>
        </Box>

        <Typography noWrap style={{ fontSize: 14, color: '#444444', marginTop: 2 }}>
          {preview}
        </Typography>

        <Box display='flex' alignItems='center' style={{ gap: 8, marginTop: 6 }}>
          {tag && <Tag label={tag} />}
          {!isRoster && item.sent.repliable && <Tag label='Replies on' outlined />}
        </Box>
      </Box>

      <Box display='flex' alignItems='center' style={{ gap: 10, flexShrink: 0 }}>
        <ChannelChip channel={channel} />
        {statuses.map((summary) => (
          <CommunicationStatusChip
            key={summary.status}
            status={summary.status}
            channel={channel}
            count={summary.count}
          />
        ))}
        <Typography style={{ fontSize: 12, color: '#646464', whiteSpace: 'nowrap' }}>
          {sentLabel(item.sortAt)}
        </Typography>
        <ChevronRight style={{ color: '#9E9E9E' }} />
      </Box>
    </Box>
  );
};
